import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { api, errorMessage, unwrap } from "@/api/client";
import { usd } from "@/api/providers";
import { canCancel, canDelete, canPause, canResume, canRetry, type RunDetail, runKey, useRunActions } from "@/api/runs";
import ConfirmDialog from "@/components/confirm-dialog";
import Icon, { type IconName } from "@/components/icon";
import IconBadge, { type BadgeTone } from "@/components/icon-badge";
import { ItemsTable } from "@/components/ItemsTable";
import {
  List,
  ListHeader,
  ListItem,
  ListItemContent,
  ListItemIcon,
  ListItemTrailing,
  ListTitle,
} from "@/components/list";
import Loader from "@/components/loader";
import PageHeader from "@/components/page-header";
import StatusTag from "@/components/status-tag";
import Text from "@/components/text";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDuration, formatWait, formatWhen, LIVE_RUN_STATES, money } from "@/lib/format";
import { failureSummary, nextStep, reasonLabel, retryClassLabel } from "@/lib/reasons";
import { cn } from "@/utils/cn";

const POLL_MS = 2500;
// Not a failure: a run buying numbers completed because its money ran out.
const BUDGET_REACHED = "provider_budget_reached";

function isLive(run: RunDetail | undefined): boolean {
  return !!run && (LIVE_RUN_STATES.has(run.state) || run.verify.pending > 0);
}

export function RunDetailPage() {
  const { runId = "" } = useParams();
  const run = useQuery({
    queryKey: runKey(runId),
    queryFn: () => unwrap(api.GET("/api/runs/{run_id}", { params: { path: { run_id: runId } } })),
    refetchInterval: (query) => (isLive(query.state.data) ? POLL_MS : false),
  });
  const live = isLive(run.data);

  if (run.isPending)
    return (
      <div className="grid place-items-center py-24 text-muted-foreground">
        <Loader />
      </div>
    );
  if (run.isError) return <Text.Danger>{errorMessage(run.error)}</Text.Danger>;
  const r = run.data;

  return (
    <>
      <PageHeader
        back={{ to: "/runs", label: "Runs" }}
        title={r.name}
        meta={
          <>
            <StatusTag state={r.state} />
            {live && <LiveMarker />}
          </>
        }
        description={describe(r)}
        actions={<Actions run={r} />}
      />

      {r.halt_reason === BUDGET_REACHED ? (
        <div className="flex items-center gap-3 rounded-xl bg-warning/12 px-(--list-inset) py-3">
          <Icon name="wallet" variant="Bold" size={20} className="text-warning" />
          <Text>
            Finished at the spend cap: <Text weight="medium">{r.counts.ok}</Text> of {r.provider?.target ?? "?"}{" "}
            accounts registered.
          </Text>
        </div>
      ) : (
        r.halt_reason && (
          <div className="flex items-center gap-3 rounded-xl bg-danger/10 px-(--list-inset) py-3">
            <Icon name="warning" variant="Bold" size={20} className="text-danger" />
            <Text>
              Stopped: <Text weight="medium">{stopLabel(r, r.halt_reason)}</Text>
              {stopDetail(r, r.halt_reason)}. {nextStep(r.halt_reason)}
            </Text>
          </div>
        )
      )}

      {r.state === "paused" && (
        <div className="flex items-center gap-3 rounded-xl bg-warning/12 px-(--list-inset) py-3">
          <Icon name="pause" variant="Bold" size={20} className="text-warning" />
          <Text>
            Paused with <Text weight="medium">{r.counts.pending}</Text> {r.counts.pending === 1 ? "number" : "numbers"}{" "}
            still to go. Continue picks up where it stopped.
          </Text>
        </div>
      )}

      {r.provider && <BoughtSection run={r} bought={r.provider} />}

      <div className="grid gap-5 lg:grid-cols-3">
        <NumbersSection run={r} />
        <ReviewSection run={r} />
        <FailuresSection run={r} />
      </div>

      <ItemsTable run={r} pollMs={live ? POLL_MS : false} />

      <div className="grid gap-5 lg:grid-cols-2">
        <SettingsSection run={r} />
        {r.rejected_lines.length > 0 && <RejectedSection run={r} />}
      </div>
    </>
  );
}

/** Why the run stopped, with the run's own limit where it is the point. */
function stopLabel(r: RunDetail, reason: string): string {
  if (reason === "provider_loss_streak" && r.provider) {
    return `${r.provider.loss_streak_limit} paid numbers failed in a row`;
  }
  return reasonLabel(reason);
}

function stopDetail(r: RunDetail, reason: string): string {
  const b = r.provider;
  if (reason === "provider_no_numbers" && b?.no_stock_wait_seconds)
    return ` for ${formatWait(b.no_stock_wait_seconds)}`;
  // A buy run has one country; a pasted list can mix them.
  if (COUNTRY_STOPS.has(reason) && b) return ` for ${b.country}`;
  if (reason === "too_many_failures" && r.failure_reasons.length) return `: ${failureSummary(r.failure_reasons)}`;
  return "";
}

// Stops about the server's setup for the number's country.
const COUNTRY_STOPS = new Set(["missing_purchase_order", "missing_product_config"]);

function describe(r: RunDetail): string {
  const type = r.submit_type === "login" ? "Login run" : "Register run";
  if (!r.started_at) return `${type}, not started`;
  // A retry runs in this same run, so the time shown is the current round's own, not
  // the wait since the run first started.
  const from = r.round_started_at ?? r.started_at;
  const round = r.round > 1 ? `Round ${r.round}, ` : "";
  const took = formatDuration(new Date(r.finished_at ?? Date.now()).getTime() - new Date(from).getTime());
  return r.finished_at
    ? `${type}, ${round}started ${formatWhen(from)}, took ${took}`
    : `${type}, ${round}started ${formatWhen(from)}, running for ${took}`;
}

function LiveMarker() {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
      <span className="relative flex size-2">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60" />
        <span className="relative inline-flex size-2 rounded-full bg-success" />
      </span>
      Live
    </span>
  );
}

function Actions({ run }: { run: RunDetail }) {
  const navigate = useNavigate();
  const { cancel, pause, resume, retry, remove } = useRunActions();
  const base = `/api/runs/${run.id}`;

  return (
    <>
      {canResume(run) && (
        <Button loading={resume.isPending} onClick={() => resume.mutate(run.id)}>
          <Icon name="play" size={18} />
          Continue
        </Button>
      )}
      {canPause(run) && (
        <Button variant="outline" loading={pause.isPending} onClick={() => pause.mutate(run.id)}>
          <Icon name="pause" size={18} />
          Pause
        </Button>
      )}
      {canCancel(run) && (
        <Button variant="outline" loading={cancel.isPending} onClick={() => cancel.mutate(run.id)}>
          <Icon name="stop" size={18} />
          Cancel run
        </Button>
      )}
      {canRetry(run) && (
        <Button
          variant={canResume(run) ? "outline" : "default"}
          loading={retry.isPending}
          onClick={() => retry.mutate(run.id)}
        >
          <Icon name="retry" size={18} />
          Retry {run.retryable_failed} failed
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <Icon name="download" size={18} />
            Export
            <Icon name="chevronDown" size={16} className="-me-1 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <ExportItem href={`${base}/export.csv`} title="All results" hint="CSV, one row per number" />
          <DropdownMenuSeparator />
          <ExportItem
            href={`${base}/failed.txt?scope=retryable`}
            title="Failed numbers worth retrying"
            hint="Ready to paste into a new run"
          />
          <ExportItem
            href={`${base}/failed.txt?scope=failed`}
            title="All failed numbers"
            hint="Including final failures"
          />
          <ExportItem
            href={`${base}/failed.txt?scope=unfinished`}
            title="Unfinished numbers"
            hint="Worth retrying, plus never started"
          />
        </DropdownMenuContent>
      </DropdownMenu>
      {canDelete(run) && (
        <ConfirmDialog
          trigger={
            <Button variant="destructive" aria-label="Delete run">
              <Icon name="trash" size={18} />
              Delete
            </Button>
          }
          title={`Delete "${run.name}"?`}
          description={`Its ${run.counts.total} rows go with it. Accounts already submitted are kept.`}
          confirmLabel="Delete run"
          destructive
          onConfirm={() => remove.mutate(run.id, { onSuccess: () => navigate("/runs") })}
        />
      )}
    </>
  );
}

function ExportItem({ href, title, hint }: { href: string; title: string; hint: string }) {
  return (
    <DropdownMenuItem asChild>
      <a href={href} download className="flex flex-col items-start gap-0.5">
        <Text weight="medium">{title}</Text>
        <Text.Hint size="caption">{hint}</Text.Hint>
      </a>
    </DropdownMenuItem>
  );
}

function StatRow({
  icon,
  tone,
  label,
  hint,
  value,
}: {
  icon: IconName;
  tone: BadgeTone;
  label: string;
  hint?: string;
  value: ReactNode;
}) {
  return (
    <ListItem>
      <ListItemIcon>
        <IconBadge tone={tone}>
          <Icon name={icon} variant="Bold" size={18} />
        </IconBadge>
      </ListItemIcon>
      <ListItemContent>
        <Text>{label}</Text>
        {hint && <Text.Hint size="caption">{hint}</Text.Hint>}
      </ListItemContent>
      <ListItemTrailing>
        <Text weight="medium" className="tabular-nums">
          {value}
        </Text>
      </ListItemTrailing>
    </ListItem>
  );
}

/** The failures counted so far, and what most of them are, while they build up. */
function repeatedFailures(run: RunDetail, limit: number): string {
  const count = `Repeated failures ${run.failure_score}, stops at ${limit}`;
  const top = run.failure_reasons[0];
  return run.failure_score > 0 && top ? `${count}, mostly ${reasonLabel(top.reason)}` : count;
}

function NumbersSection({ run }: { run: RunDetail }) {
  const c = run.counts;
  const settled = c.ok + c.failed;
  const pct = c.total ? Math.round((settled / c.total) * 100) : 0;
  const limit = run.settings.failure_limit;
  return (
    <List className="pb-1.5">
      <ListHeader>
        <ListTitle>Numbers</ListTitle>
        <Text.Hint className="tabular-nums">
          {settled} of {c.total} done
        </Text.Hint>
      </ListHeader>
      <div className="px-(--list-inset) pt-1 pb-2">
        <Progress value={pct} className="h-1.5" />
      </div>
      <StatRow icon="check" tone="green" label="Registered" value={c.ok} />
      <StatRow
        icon="close"
        tone="red"
        label="Failed"
        hint={limit > 0 ? repeatedFailures(run, limit) : undefined}
        value={c.failed}
      />
      <StatRow icon="clock" tone="blue" label="In progress" value={c.running} />
      <StatRow icon="runs" tone="gray" label="Not started" value={c.pending} />
    </List>
  );
}

function BoughtSection({ run, bought: b }: { run: RunDetail; bought: NonNullable<RunDetail["provider"]> }) {
  return (
    <List className="pb-1.5">
      <ListHeader>
        <ListTitle>Numbers bought</ListTitle>
        <Text.Hint>
          {b.account_label ?? b.provider_title} · {b.country}, server {b.slot}
          {b.operator && `, range ${b.operator}`}
        </Text.Hint>
      </ListHeader>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5">
        <StatRow icon="cart" tone="blue" label="Bought" hint={`${b.in_use} in use now`} value={b.bought} />
        <StatRow icon="check" tone="green" label="Registered" hint={`Target ${b.target}`} value={`${run.counts.ok}`} />
        <StatRow
          icon="retry"
          tone="teal"
          label="Refunded"
          hint={
            b.refund_pending > 0
              ? `${usd(b.refunded_amount)}, ${b.refund_pending} more pending`
              : usd(b.refunded_amount)
          }
          value={b.refunded}
        />
        <StatRow
          icon="warning"
          tone="red"
          label="Paid, then failed"
          hint={`${b.loss_streak} in a row, stops at ${b.loss_streak_limit}`}
          value={b.lost}
        />
        <StatRow
          icon="wallet"
          tone="violet"
          label="Spent"
          hint={`Cap ${usd(b.max_spend)}, up to ${usd(b.max_price)} each`}
          value={usd(b.spent)}
        />
      </div>
      {b.operators.length > 1 && <ByOperator rows={b.operators} />}
    </List>
  );
}

/** Which number ranges the run's numbers came from, and how they did. Only worth
 * showing when a run took whatever the server gave and so has more than one. */
function ByOperator({ rows }: { rows: NonNullable<RunDetail["provider"]>["operators"] }) {
  return (
    <div className="border-t">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Number range</TableHead>
            <TableHead className="text-end">Bought</TableHead>
            <TableHead className="text-end">Registered</TableHead>
            <TableHead className="text-end">Spent</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.operator}>
              <TableCell className="font-medium">{r.operator}</TableCell>
              <TableCell className="text-end tabular-nums">{r.bought.toLocaleString()}</TableCell>
              <TableCell className="text-end tabular-nums">
                {r.registered.toLocaleString()}
                <span className="text-muted-foreground">
                  {r.bought > 0 && ` · ${Math.round((r.registered / r.bought) * 100)}%`}
                </span>
              </TableCell>
              <TableCell className="text-end tabular-nums">{usd(r.spent)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ReviewSection({ run }: { run: RunDetail }) {
  const v = run.verify;
  return (
    <List className="pb-1.5">
      <ListHeader>
        <ListTitle>Review</ListTitle>
        {v.pending > 0 && <Text.Hint>checking…</Text.Hint>}
      </ListHeader>
      <StatRow icon="check" tone="green" label="Accepted" value={v.accepted} />
      <StatRow icon="close" tone="red" label="Rejected" value={v.rejected} />
      <StatRow icon="clock" tone="yellow" label="Waiting for review" value={v.pending} />
      <StatRow
        icon="price"
        tone="teal"
        label="Payout if accepted"
        hint="Registered accounts not rejected"
        value={money(run.paid_if_accepted)}
      />
    </List>
  );
}

function FailuresSection({ run }: { run: RunDetail }) {
  const base = `/api/runs/${run.id}/failed.txt`;
  // A reason can show up under two retry classes; its file holds both rows' numbers.
  const perReason = new Map<string, number>();
  for (const r of run.reasons) perReason.set(r.reason, (perReason.get(r.reason) ?? 0) + r.count);

  return (
    <List className="pb-1.5">
      <ListHeader>
        <ListTitle>Failures</ListTitle>
        {run.counts.failed > 0 && (
          <a
            href={`${base}?scope=failed`}
            download
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80"
          >
            <Icon name="download" size={16} />
            Download all ({run.counts.failed})
          </a>
        )}
      </ListHeader>
      {run.reasons.length === 0 ? (
        <Text.Hint className="px-(--list-inset) py-3">No failures.</Text.Hint>
      ) : (
        run.reasons.map((r) => (
          <ListItem key={`${r.reason}-${r.retry_class}`}>
            <ListItemContent>
              <Text className="truncate">{reasonLabel(r.reason)}</Text>
              <Text.Hint size="caption" className="truncate">
                {retryClassLabel(r.retry_class)} · {r.reason}
              </Text.Hint>
            </ListItemContent>
            <ListItemTrailing className="gap-2">
              <Badge
                className={r.retry_class === "terminal" ? "bg-muted text-muted-foreground" : "bg-danger text-white"}
              >
                {r.count}
              </Badge>
              <Button variant="ghost" size="icon" className="size-9 justify-center text-primary" asChild>
                <a
                  href={`${base}?reason=${encodeURIComponent(r.reason)}`}
                  download
                  aria-label={`Download the ${perReason.get(r.reason)} numbers that failed with ${reasonLabel(r.reason)}`}
                  title="Download these numbers"
                >
                  <Icon name="download" size={18} />
                </a>
              </Button>
            </ListItemTrailing>
          </ListItem>
        ))
      )}
    </List>
  );
}

type SettingRow = [string, ReactNode];

function SettingsSection({ run }: { run: RunDetail }) {
  const s = run.settings;
  const b = run.provider;
  const [open, setOpen] = useState(false);
  const failureLimit = s.failure_limit || "Never";
  const codeWait = `${s.otp_poll_timeout} s, checked every ${s.otp_poll_interval} s`;
  // A buy run shows its limits apart, as its form groups them. It never uses a prefix
  // or skips settled numbers, so those are left out.
  const groups: [string | null, SettingRow[]][] = b
    ? [
        [
          "Limits",
          [
            ["Accounts to register", b.target],
            ["Max price per number", usd(b.max_price)],
            ["Max spend", usd(b.max_spend)],
            ["Give up after no stock", b.no_stock_wait_seconds ? formatWait(b.no_stock_wait_seconds) : "At once"],
            ["Stop after failed paid numbers", `${b.loss_streak_limit} in a row`],
            ["Stop after repeated failures", failureLimit],
            ["Number range", b.operator ?? "Any"],
            ["Check each number first", b.check_numbers ? "On" : "Off"],
          ],
        ],
        [
          "Settings",
          [
            ["Numbers at once", s.concurrency],
            ["Attempts per number", s.max_attempts],
            ["Stagger", `up to ${s.stagger_seconds} s`],
            ["Send rate", `${s.send_rate_per_second} per second`],
            ["Code wait", codeWait],
            ["Auto email", s.auto_email ? "On" : "Off"],
          ],
        ],
      ]
    : [
        [
          null,
          [
            ["Numbers at once", s.concurrency],
            ["Stagger", `up to ${s.stagger_seconds} s`],
            ["Send rate", `${s.send_rate_per_second} per second`],
            ["Attempts per number", s.max_attempts],
            ["Stop after repeated failures", failureLimit],
            ["Prefix", s.prefix || "none"],
            ["Auto email", s.auto_email ? "On" : "Off"],
            ["Skip settled numbers", s.skip_settled ? "Yes" : "No"],
            ["Code wait", codeWait],
          ],
        ],
      ];
  return (
    <List className={cn("self-start", b && "lg:col-span-2")}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <ListHeader className="pb-2.5">
          <CollapsibleTrigger asChild>
            <button type="button" className="group flex w-full items-center justify-between gap-1">
              <ListTitle>Run settings</ListTitle>
              <span className="flex items-center gap-1 text-tg-hint">
                <Text.Hint>{open ? "Hide" : "Show"}</Text.Hint>
                <Icon
                  name="chevronDown"
                  size={18}
                  className="transition-transform group-data-[state=open]:rotate-180"
                />
              </span>
            </button>
          </CollapsibleTrigger>
        </ListHeader>
        <CollapsibleContent>
          <div className={cn("pb-1.5", groups.length > 1 && "grid lg:grid-cols-2 lg:gap-x-6")}>
            {groups.map(([title, rows]) => (
              <div key={title ?? "all"} className="min-w-0">
                {title && (
                  <Text.Hint
                    as="h3"
                    size="caption"
                    weight="medium"
                    className="px-(--list-inset) pt-3 pb-1.5 tracking-wide uppercase"
                  >
                    {title}
                  </Text.Hint>
                )}
                <dl className="divide-y divide-border">
                  {rows.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-4 px-(--list-inset) py-2.5">
                      <dt>
                        <Text.Hint>{k}</Text.Hint>
                      </dt>
                      <dd>
                        <Text>{v}</Text>
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </List>
  );
}

function RejectedSection({ run }: { run: RunDetail }) {
  const [open, setOpen] = useState(false);
  return (
    <List className="self-start">
      <Collapsible open={open} onOpenChange={setOpen}>
        <ListHeader className="pb-2.5">
          <CollapsibleTrigger asChild>
            <button type="button" className="group flex w-full items-center justify-between gap-1">
              <ListTitle>Lines left out of this run</ListTitle>
              <span className="flex items-center gap-1 text-tg-hint">
                <Text.Hint>{run.rejected_lines.length}</Text.Hint>
                <Icon
                  name="chevronDown"
                  size={18}
                  className="transition-transform group-data-[state=open]:rotate-180"
                />
              </span>
            </button>
          </CollapsibleTrigger>
        </ListHeader>
        <CollapsibleContent>
          <div className="max-h-80 divide-y divide-border overflow-y-auto pb-1.5">
            {run.rejected_lines.map((l) => (
              <div key={l.line_no} className="flex flex-col gap-0.5 px-(--list-inset) py-2.5">
                <Text className="font-mono text-[0.8rem] break-all">
                  <Text.Hint size="caption">Line {l.line_no} </Text.Hint>
                  {l.raw_line}
                </Text>
                {l.settled ? (
                  <Text.Hint size="caption">{l.reason}</Text.Hint>
                ) : (
                  <Text.Danger size="caption">{l.reason}</Text.Danger>
                )}
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </List>
  );
}
